import type { EnemyKind } from './enemies'
import type { MapId } from './maps'

export type FormationId = 'DATA_STREAM' | 'PACKET_BURST' | 'GUARDIAN_ESCORT' | 'FORK_PINCER' | 'SIEGE_COLUMN'
export type FormationOrder = 'DEFAULT' | 'FAST_FIRST' | 'HEAVY_FIRST'
export type FormationPathing = 'ROUND_ROBIN' | 'GROUPED_LANE' | 'MIRRORED'

export interface FormationDefinition {
  readonly id: FormationId
  readonly name: string
  readonly glyph: string
  readonly description: string
  readonly color: number
  readonly cssColor: string
  readonly groupSize: number
  readonly intraGroupInterval: number
  readonly regroupInterval: number
  readonly order: FormationOrder
  readonly pathing: FormationPathing
}

export const FORMATIONS: Record<FormationId, FormationDefinition> = {
  DATA_STREAM: {
    id: 'DATA_STREAM', name: 'DATA STREAM', glyph: '››', color: 0x35d8ea, cssColor: '#35d8ea',
    description: 'Steady pressure distributed across every available uplink.',
    groupSize: 4, intraGroupInterval: 0.78, regroupInterval: 1.12, order: 'DEFAULT', pathing: 'ROUND_ROBIN',
  },
  PACKET_BURST: {
    id: 'PACKET_BURST', name: 'PACKET BURST', glyph: '≋', color: 0xff4fd8, cssColor: '#ff4fd8',
    description: 'Fast clusters arrive in tight bursts, followed by a short regroup window.',
    groupSize: 3, intraGroupInterval: 0.3, regroupInterval: 1.85, order: 'FAST_FIRST', pathing: 'ROUND_ROBIN',
  },
  GUARDIAN_ESCORT: {
    id: 'GUARDIAN_ESCORT', name: 'GUARDIAN ESCORT', glyph: '◆', color: 0x47f2a5, cssColor: '#47f2a5',
    description: 'Armored leaders advance with support processes positioned behind them.',
    groupSize: 4, intraGroupInterval: 0.55, regroupInterval: 1.5, order: 'HEAVY_FIRST', pathing: 'GROUPED_LANE',
  },
  FORK_PINCER: {
    id: 'FORK_PINCER', name: 'FORK PINCER', glyph: '⋔', color: 0x7be9ff, cssColor: '#7be9ff',
    description: 'Mirrored pairs strike alternate uplinks before converging on the Core.',
    groupSize: 2, intraGroupInterval: 0.2, regroupInterval: 1.45, order: 'DEFAULT', pathing: 'MIRRORED',
  },
  SIEGE_COLUMN: {
    id: 'SIEGE_COLUMN', name: 'SIEGE COLUMN', glyph: '⬢', color: 0xffb347, cssColor: '#ffb347',
    description: 'Heavy units lead concentrated columns while lighter processes screen the rear.',
    groupSize: 3, intraGroupInterval: 0.48, regroupInterval: 1.7, order: 'HEAVY_FIRST', pathing: 'GROUPED_LANE',
  },
}

export const FORMATION_IDS = Object.keys(FORMATIONS) as FormationId[]

export const FORMATION_SCHEDULES: Record<MapId, readonly FormationId[]> = {
  GENESIS_NETWORK: ['DATA_STREAM', 'PACKET_BURST', 'GUARDIAN_ESCORT', 'SIEGE_COLUMN', 'SIEGE_COLUMN'],
  NEON_EXCHANGE: ['DATA_STREAM', 'PACKET_BURST', 'PACKET_BURST', 'GUARDIAN_ESCORT', 'PACKET_BURST'],
  VALIDATOR_HEIGHTS: ['FORK_PINCER', 'FORK_PINCER', 'GUARDIAN_ESCORT', 'FORK_PINCER', 'SIEGE_COLUMN'],
}

export const FORMATION_KIND_PRIORITY: Record<FormationOrder, readonly EnemyKind[]> = {
  DEFAULT: ['BOT', 'PAPER_HAND', 'BOT', 'FUD_BOT', 'LIQUIDATOR', 'WHALE'],
  FAST_FIRST: ['PAPER_HAND', 'BOT', 'FUD_BOT', 'LIQUIDATOR', 'WHALE'],
  HEAVY_FIRST: ['LIQUIDATOR', 'FUD_BOT', 'BOT', 'PAPER_HAND', 'WHALE'],
}
